import { useEffect, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import CustomText from "../components/CustomText";
import ImageLoader from "../components/ImageLoader";

const CustomHospitalAccess = () => {
  const contentRef = useRef(null);
  const leftCircleRef = useRef(null);
  const rightCircleRef = useRef(null);
  const targetCardRef = useRef(null);

  const [lines, setLines] = useState(null);

  useEffect(() => {
    const calculateLines = () => {
      if (
        !contentRef.current ||
        !leftCircleRef.current ||
        !rightCircleRef.current ||
        !targetCardRef.current
      ) {
        return;
      }

      const contentRect = contentRef.current.getBoundingClientRect();
      const leftCircleRect = leftCircleRef.current.getBoundingClientRect();
      const rightCircleRect = rightCircleRef.current.getBoundingClientRect();
      const cardRect = targetCardRef.current.getBoundingClientRect();

      // Punto de salida del círculo izquierdo
      const leftStart = {
        x: leftCircleRect.left - contentRect.left + leftCircleRect.width / 2,
        y: leftCircleRect.top - contentRect.top + leftCircleRect.height / 2,
      };

      // Punto de salida del círculo derecho
      const rightStart = {
        x: rightCircleRect.left - contentRect.left + rightCircleRect.width / 2,
        y: rightCircleRect.top - contentRect.top + rightCircleRect.height / 2,
      };

      // Puntos de llegada a la tarjeta
      const cardLeft = {
        x: cardRect.left - contentRect.left,
        y: cardRect.top - contentRect.top + cardRect.height / 2,
      };

      const joinPoint = {
        x: cardLeft.x,
        y: 300,
      };

      setLines({
        left: { start: leftStart, end: joinPoint },
        right: { start: rightStart, end: joinPoint },
        join: joinPoint,
        card: cardLeft,
      });
    };
    const timeout = setTimeout(calculateLines, 100);
    window.addEventListener("resize", calculateLines);
    const resizeObserver = new ResizeObserver(calculateLines);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", calculateLines);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Grid container sx={design.Container}>
      <Grid size={12}>
        <CustomText
          color="#8625B8"
          fontWeight={500}
          fontFamily="Nunito"
          textAlign="center"
          sx={design.Subtitle}
        >
          Movilidad y Acceso
        </CustomText>
        <CustomText
          color="#304795"
          fontWeight={700}
          fontFamily="Nunito"
          textAlign="center"
          sx={design.Title}
        >
          2 formas de accesar
        </CustomText>
        <CustomText
          color="#111111"
          fontWeight={500}
          fontFamily="Nunito"
          textAlign="center"
          sx={design.Description}
        >
          Diseñamos el predio para que llegar sea tan fácil como el servicio que
          vas a recibir.
        </CustomText>
      </Grid>
      <Grid container size={12} ref={contentRef} sx={design.Content}>
        <Box sx={design.AccessLines}>
          {lines && (
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${contentRef.current?.clientWidth || 1100} ${
                contentRef.current?.clientHeight || 500
              }`}
              preserveAspectRatio="none"
            >
              {/* ======================= IZQUIERDO ================= */}
              <path
                d={`
                  M ${lines.left.start.x} ${lines.left.start.y}
                  L ${lines.left.start.x - 65} ${lines.left.start.y}
                  Q ${lines.left.start.x - 80}  ${lines.left.start.y} ${lines.left.start.x - 80} ${lines.left.start.y + 15}
                  L ${lines.left.start.x - 80} ${lines.left.end.y + 70}
                  Q ${lines.left.start.x - 80} ${lines.left.end.y + 90} ${lines.left.start.x - 60} ${lines.left.end.y + 90}
                  L ${lines.left.end.x - 60} ${lines.left.end.y + 90}
                  C ${lines.left.end.x - 40} ${lines.left.end.y + 90} ${lines.left.end.x - 40} ${lines.left.end.y + 82} ${lines.left.end.x - 40} ${lines.left.end.y + 68}
                  L ${lines.left.end.x - 40} ${lines.left.end.y + 40}
                  C ${lines.left.end.x - 40} ${lines.left.end.y + 15} ${lines.left.end.x - 32} ${lines.left.end.y} ${lines.left.end.x - 15} ${lines.left.end.y}
                  L ${lines.left.end.x} ${lines.left.end.y}
                `}
                fill="none"
                stroke="#D9272E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* ====================== DERECHO ==================== */}
              <path
                d={`
                  M ${lines.right.start.x} ${lines.right.start.y}
                  L ${lines.right.start.x + 25} ${lines.right.start.y}
                  Q ${lines.right.start.x + 40} ${lines.right.start.y} ${lines.right.start.x + 40} ${lines.right.start.y + 15}
                  L ${lines.right.start.x + 40} ${lines.right.end.y - 20}
                  Q ${lines.right.start.x + 40} ${lines.right.end.y} ${lines.right.start.x + 60} ${lines.right.end.y}
                  L ${lines.right.end.x} ${lines.right.end.y}
                `}
                fill="none"
                stroke="#D9272E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx={lines.join.x}
                cy={lines.join.y}
                r="4"
                fill="#D9272E"
              />
            </svg>
          )}
        </Box>
        <Grid size={{ xs: 12, md: 6 }} sx={design.ImageContainer}>
          <Box sx={design.ImageWrapper}>
            <ImageLoader
              src="https://centromedicomedam.com/IMG/centromedicoMEDAM/Helipuerto_infrstructura.jpg"
              alt="Accesos y estacionamientos Centro Médico MEDAM"
              width={{ xs: "100%", sm: "100%", md: "100%", lg: "100%" }}
            />
            <Box ref={leftCircleRef} sx={design.RedCircleLeft} />
            <Box ref={rightCircleRef} sx={design.RedCircleRight} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 5, xl: 5 }} sx={design.CardsContainer}>
          {accessItems.map((item, index) => (
            <Box
              key={index}
              ref={index === 2 ? targetCardRef : null}
              sx={design.Card}
            >
              <Box sx={design.IconCircle} />
              <Box sx={design.CardContent}>
                <CustomText
                  color="#000000"
                  fontWeight={800}
                  fontFamily="Nunito"
                  textAlign="left"
                  sx={design.CardTitle}
                >
                  {item.title}
                </CustomText>
                <CustomText
                  color="#000000"
                  fontWeight={300}
                  fontFamily="Nunito"
                  textAlign="left"
                  sx={design.CardTitle}
                >
                  {item.description}
                </CustomText>
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CustomHospitalAccess;

const design = {
  Subtitle: {
    fontSize: { xs: 16, sm: 15, md: 15, lg: 15 },
    lineHeight: 1.2,
    mb: { xs: 1.5, md: 1 },
  },
  Title: {
    fontSize: { xs: 33, sm: 36, md: 40, lg: 43 },
    lineHeight: 1.15,
    letterSpacing: "-0.5px",
    mb: { xs: 1.5, md: 1.5 },
  },
  Description: {
    fontSize: { xs: 16, sm: 15, md: 16, lg: 17 },
    lineHeight: 1.4,
    mx: "auto",
    mb: { xs: 4, sm: 5, md: 4 },
  },
  Container: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    py: { xs: 4, sm: 5, md: 6 },
    px: { xs: 2, sm: 3, md: 5, lg: 8 },
  },
  Content: {
    mt: { xs: 4, sm: 5, md: 4 },
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    position: "relative",
  },
  ImageContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  ImageWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: 700,
    "& img": {
      width: "100%",
      height: "auto",
      display: "block",
    },
  },
  RedCircleLeft: {
    position: "absolute",
    left: { xs: "1%", md: "3%" },
    bottom: { xs: "12%", md: "33%" },
    width: { xs: 0, sm: 19, md: 20 },
    height: { xs: 0, sm: 19, md: 20 },
    borderRadius: "50%",
    backgroundColor: "#D9272E",
  },
  RedCircleRight: {
    position: "absolute",
    right: { xs: "1%", md: "3%" },
    bottom: { xs: "12%", md: "33%" },
    width: { xs: 0, sm: 19, md: 20 },
    height: { xs: 0, sm: 19, md: 20 },
    borderRadius: "50%",
    backgroundColor: "#D9272E",
  },
  CardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 1.5, md: 1.8 },
    pl: { xs: 0, md: 2, lg: 3 },
    pr: { xs: 0, md: 2 },
  },
  Card: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    p: 1.2,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    boxShadow: "10px 0px 10px rgba(0, 0, 0, 0.18)",
    transition: "all 0.25s ease",
    "&:hover": {
      transform: "translateX(4px)",
      boxShadow: "0px 5px 12px rgba(0, 0, 0, 0.20)",
    },
  },
  IconCircle: {
    flexShrink: 0,
    width: { xs: 17, sm: 18, md: 17 },
    height: { xs: 17, sm: 18, md: 17 },
    borderRadius: "50%",
    mt: 0.6,
    mr: 1.5,
    background:
      "linear-gradient(135deg, #9B7CF6 0%, #B68CF5 45%, #68C8FF 100%)",
  },
  CardContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  CardTitle: {
    fontSize: { xs: 15, sm: 16, md: 17, lg: 18 },
    lineHeight: 1.2,
    mb: 0.4,
  },
  CardDescription: {
    fontSize: { xs: 10, sm: 11, md: 10, lg: 11 },
    lineHeight: 1.2,
  },
  AccessLines: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    zIndex: 2,
    pointerEvents: "none",
    display: {
      xs: "none",
      md: "block",
    },
    "& svg": {
      display: "block",
      width: "100%",
      height: "100%",
      overflow: "visible",
    },
  },
};

const accessItems = [
  {
    title: "2 Estacionamientos",
    description:
      "170 cajones: 40 en sótano y 130 exteriores, con acceso directo a la torre.",
  },
  {
    title: "Zonas comunes",
    description:
      "Circulación pensada para separar el flujo de visitantes, pacientes y personal médico.",
  },
  {
    title: "Accesos por ambos extremos",
    description:
      "Entradas independientes en cada extremo del predio para agilizar la llegada según el servicio.",
  },
];
